import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, ShieldCheck } from "lucide-react";
import { seguimientoApi } from "./api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { ApplicationsTable, type Column } from "../applications/shared/ApplicationsTable";
import { Badge } from "./ui";
import { useToast } from "@/app/providers/Toaster";
import type { S360FunnelClub } from "./types";

/**
 * Seguridad 360 como puerta de entrada a los clubes (roadmap del backend en
 * docs/seguridad360/). Embudo M1 a M4: clubes activados con origen
 * «seguridad360» → uso real en 30 días → primer evento con venta → días hasta
 * esa venta. Abajo, los clubes nuevos sin origen para marcarlos.
 */

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString("es-CL", { day: "numeric", month: "short", year: "numeric" }) : "—";

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="nx-card p-4 flex flex-col gap-1">
      <span className="nx-eyebrow">{label}</span>
      <span className="text-2xl font-semibold text-fg tabular-nums">{value}</span>
      {sub ? <span className="text-2xs text-fg-subtle">{sub}</span> : null}
    </div>
  );
}

export function Seguridad360Pane() {
  const qc = useQueryClient();
  const { push } = useToast();
  const funnel = useQuery({ queryKey: ["seg", "s360"], queryFn: () => seguimientoApi.s360Funnel() });

  const mark = useMutation({
    mutationFn: (clubId: number) => seguimientoApi.setClubOrigin(clubId, "seguridad360"),
    onSuccess: (res) => {
      push({ kind: "success", message: `${res.club.name} quedó con origen Seguridad 360.` });
      qc.invalidateQueries({ queryKey: ["seg", "s360"] });
    },
    onError: (err: any) => {
      push({ kind: "error", message: err?.response?.data?.error || "No pudimos guardar el origen." });
    },
  });

  if (funnel.isLoading) return <LoadingBlock label="Cargando el embudo…" />;
  if (funnel.isError || !funnel.data) return <ErrorBlock onRetry={() => funnel.refetch()} />;

  const { summary: s, clubs, unmarked } = funnel.data;
  const rows = clubs.map((c) => ({ ...c }));

  const columns: Column<S360FunnelClub>[] = [
    {
      key: "club",
      header: "Club",
      cell: (c) => (
        <div>
          <div className="font-medium text-fg">{c.name}</div>
          <div className="text-2xs text-fg-subtle break-all">{c.ownerEmail || "Sin dueño"}</div>
        </div>
      ),
    },
    {
      key: "activated",
      header: "Activado",
      cell: (c) => <span className="text-xs text-fg-muted whitespace-nowrap">{fmtDate(c.activatedAt)}</span>,
      width: "min",
    },
    {
      key: "use",
      header: "Uso 30 d",
      cell: (c) => (
        <div className="flex flex-col items-start gap-1">
          <Badge tone={c.active ? "success" : "neutral"} className="whitespace-nowrap">
            {c.active ? "Lo usa" : "Sin uso"}
          </Badge>
          <span className="text-2xs text-fg-subtle tabular-nums whitespace-nowrap">
            {c.reports30d} reportes · {c.lookups30d} consultas
          </span>
        </div>
      ),
    },
    {
      key: "team",
      header: "Equipo de puerta",
      cell: (c) => <span className="text-xs tabular-nums">{c.staffCount}</span>,
      align: "right",
      width: "min",
    },
    {
      key: "sale",
      header: "Primera venta",
      cell: (c) =>
        c.soldBeforeActivation ? (
          <Badge tone="info">Ya vendía</Badge>
        ) : c.daysToFirstSale !== null ? (
          <div className="flex flex-col">
            <span className="text-xs text-fg whitespace-nowrap">{fmtDate(c.firstSaleAt)}</span>
            <span className="text-2xs text-fg-subtle tabular-nums whitespace-nowrap">a los {c.daysToFirstSale} días</span>
          </div>
        ) : (
          <span className="text-2xs text-fg-subtle whitespace-nowrap">Todavía no</span>
        ),
      width: "min",
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-fg-muted inline-flex items-center gap-1.5">
          <ShieldCheck size={14} className="text-accent" />
          Clubes que entraron por Seguridad 360. Uso real = al menos un reporte o una consulta en
          puerta en los últimos 30 días. Primera venta = primera entrada pagada con precio mayor a 0.
        </span>
        <button onClick={() => funnel.refetch()} className="nx-btn-secondary ml-auto" disabled={funnel.isFetching}>
          <RefreshCw size={14} className={funnel.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Activados" value={String(s.activated)} sub="Meta: 5 al 15-nov · 10 al 13-dic" />
        <Kpi
          label="Con uso real"
          value={String(s.active)}
          sub={s.activeRate === null ? "Meta: 60 %" : `${s.activeRate} % · meta 60 %`}
        />
        <Kpi
          label="Primer evento con venta"
          value={String(s.firstSale)}
          sub={`Meta: 3 al 13-dic${s.alreadySelling ? ` · ${s.alreadySelling} ya vendían` : ""}`}
        />
        <Kpi
          label="Días hasta la venta"
          value={s.medianDaysToFirstSale === null ? "—" : String(s.medianDaysToFirstSale)}
          sub="Mediana · meta 30 o menos"
        />
      </div>

      <ApplicationsTable<S360FunnelClub>
        rows={rows}
        columns={columns}
        page={1}
        pageSize={rows.length || 1}
        total={rows.length}
        onPageChange={() => {}}
        emptyLabel="Todavía no hay clubes con origen Seguridad 360. Se marcan al registrarlos (Productores › Registrar productor) o abajo."
      />

      <div className="nx-card p-4 flex flex-col gap-3">
        <div>
          <h2 className="nx-h2">Clubes nuevos sin origen</h2>
          <p className="text-2xs text-fg-subtle mt-1">
            Creados en los últimos 60 días sin marcar de dónde llegaron. Si alguno entró por Seguridad
            360, márcalo para que cuente en el embudo.
          </p>
        </div>
        {unmarked.length === 0 ? (
          <span className="text-xs text-fg-muted">No hay clubes nuevos sin origen.</span>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {unmarked.map((c) => (
              <li key={c.id} className="flex items-center gap-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-fg">{c.name}</div>
                  <div className="text-2xs text-fg-subtle break-all">
                    {c.ownerEmail || "Sin dueño"} · creado el {fmtDate(c.createdAt)}
                  </div>
                </div>
                <button
                  className="nx-btn-secondary"
                  onClick={() => mark.mutate(c.id)}
                  disabled={mark.isPending}
                >
                  Marcar Seguridad 360
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
